import React from 'react';
import { Box, Typography } from '@mui/material';
import { getColorHex, getColorLabel } from '../../utils/colors';

interface ColorChipProps {
  color?: string;
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
  variant?: 'standard' | 'outlined';
}

/**
 * Reusable ColorChip component for displaying colors consistently across the app
 * Used in board stages, workflow statuses, and other color-coded elements
 */
export const ColorChip: React.FC<ColorChipProps> = ({
  color,
  size = 'medium',
  showLabel = true,
  variant = 'outlined',
}) => {
  if (!color) {
    return showLabel ? <Typography variant="body2">-</Typography> : null;
  }

  const colorHex = getColorHex(color);
  const colorLabel = getColorLabel(color);

  // Size configurations
  const sizeConfig = {
    small: { width: 16, height: 16, fontSize: '0.75rem' },
    medium: { width: 20, height: 20, fontSize: '0.875rem' },
    large: { width: 24, height: 24, fontSize: '1rem' },
  };

  const { width, height, fontSize } = sizeConfig[size];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{
          width,
          height,
          borderRadius: '50%',
          bgcolor: colorHex,
          border: variant === 'outlined' ? '2px solid' : 'none',
          borderColor: variant === 'outlined' ? 'divider' : 'transparent',
          flexShrink: 0,
        }}
      />
      {showLabel && (
        <Typography
          variant="body2"
          sx={{
            textTransform: 'capitalize',
            fontSize,
          }}
        >
          {colorLabel}
        </Typography>
      )}
    </Box>
  );
};